import { TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import { Suspense, lazy } from "react";
import { Toaster } from "react-hot-toast";

import { useFeatureflags, useSavedAddresses } from "@web-app/api";
import { User, UserRole } from "@web-app/types";

import Navigation from "../common/Navigation";

import { LoadingFallback } from "./components/LoadingFallback";
import { SidebarTab } from "./components/SidebarTab";

// Lazy load all tab components
const ProfileTab = lazy(() => import("./ProfileTab"));
const WorkspaceTab = lazy(() => import("./WorkspaceTab"));
const UsersTab = lazy(() => import("./UsersTab"));
const NotificationTab = lazy(() => import("./NotificationTab"));
const AppsTab = lazy(() => import("./AppsTab"));
const AddressBookTab = lazy(() => import("./AddressBookTab"));
const CustomFieldsTab = lazy(() => import("./CustomFieldsTab"));
const FormBuilderTab = lazy(() => import("./FormBuilderTab"));
const ManagerDashboardTab = lazy(() => import("./ManagerDashboardTab"));

export default function Settings({
  currentUser,
  logout,
}: {
  currentUser: User;
  logout: () => void;
}) {
  const { data: savedAddressesData, isLoading: isAddressFetchingLoading } =
    useSavedAddresses(currentUser.currentWorkspace.id);
  const { hasFeature } = useFeatureflags();
  const isManager = currentUser.roles.some(
    (userRole) => userRole.role === UserRole.MANAGER,
  );

  return (
    <div className="h-screen flex flex-col">
      <title>FULFLLD | Cloud Delivery Management</title>
      <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      <link
        rel="icon"
        href="https://cdn-assets-app.fulflld.com/favicon.png"
        type="image/png"
      />
      <style>{"html { overflow: hidden; }"}</style>
      <Navigation logout={logout} />
      <Toaster position="top-center" />
      <div className="ml-20 flex flex-1 overflow-hidden">
        <TabGroup className="flex w-full">
          <TabList
            className="flex h-full w-[300px] flex-col gap-2 border-r p-2 bg-gray-100"
            data-testid="settings-page-sidebar"
          >
            <SidebarTab icon="user" label="My Profile" selectedIcon="fasUser" />
            <SidebarTab
              icon="globe"
              label="Workspace"
              selectedIcon="fasGlobe"
            />
            <SidebarTab icon="users" label="Users" selectedIcon="fasUsers" />
            <SidebarTab
              icon="notificationBell"
              label="Notification"
              selectedIcon="fasNotificationBell"
            />
            <SidebarTab icon="apps" label="Apps" selectedIcon="faApps" />
            <SidebarTab
              icon="addressBook"
              label="Address Book"
              selectedIcon="addressBook"
            />
            {!hasFeature("formBuilder") && (
              <SidebarTab
                icon="penField"
                label="Custom Fields"
                selectedIcon="fasPenField"
              />
            )}
            {hasFeature("formBuilder") && (
              <SidebarTab
                icon="form"
                label="Order Fields"
                selectedIcon="form"
              />
            )}
            {isManager && (
              <SidebarTab
                icon="chartMixedLight"
                label="Manager Dashboard"
                selectedIcon="chartMixed"
              />
            )}
          </TabList>
          <TabPanels className="flex-1 h-full overflow-y-auto">
            <TabPanel className="h-full">
              <Suspense fallback={<LoadingFallback />}>
                <ProfileTab user={currentUser} />
              </Suspense>
            </TabPanel>
            <TabPanel className="h-full">
              <Suspense fallback={<LoadingFallback />}>
                <WorkspaceTab workspace={currentUser.currentWorkspace} />
              </Suspense>
            </TabPanel>
            <TabPanel className="h-full">
              <Suspense fallback={<LoadingFallback />}>
                <UsersTab workspace={currentUser.currentWorkspace} />
              </Suspense>
            </TabPanel>
            <TabPanel className="h-full">
              <Suspense fallback={<LoadingFallback />}>
                <NotificationTab workspace={currentUser.currentWorkspace} />
              </Suspense>
            </TabPanel>
            <TabPanel className="h-full">
              <Suspense fallback={<LoadingFallback />}>
                <AppsTab workspace={currentUser.currentWorkspace} />
              </Suspense>
            </TabPanel>
            <TabPanel className="h-full">
              <Suspense fallback={<LoadingFallback />}>
                <AddressBookTab
                  addresses={savedAddressesData}
                  isLoading={isAddressFetchingLoading}
                  currentWorkspaceId={currentUser.currentWorkspace.id}
                />
              </Suspense>
            </TabPanel>
            {!hasFeature("formBuilder") && (
              <TabPanel className="h-full">
                <Suspense fallback={<LoadingFallback />}>
                  <CustomFieldsTab
                    workspaceId={currentUser.currentWorkspace.id}
                  />
                </Suspense>
              </TabPanel>
            )}
            {hasFeature("formBuilder") && (
              <TabPanel className="h-full">
                <Suspense fallback={<LoadingFallback />}>
                  <FormBuilderTab
                    workspaceId={currentUser.currentWorkspace.id}
                  />
                </Suspense>
              </TabPanel>
            )}
            {isManager && (
              <TabPanel className="h-full">
                <Suspense fallback={<LoadingFallback />}>
                  <ManagerDashboardTab />
                </Suspense>
              </TabPanel>
            )}
          </TabPanels>
        </TabGroup>
      </div>
    </div>
  );
}
